import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Configure,
  useHits,
  usePagination,
} from 'react-instantsearch-hooks-web';
import { useInView } from 'react-intersection-observer';
import GoogleMap from 'google-maps-react-markers';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { parse, remove, stringify } from 'url-params-method';
import useSupercluster from 'use-supercluster';

import X from '@/icons/light/x.svg';
import { Cluster, Marker } from '@/atoms';
import { CardFlat } from '@/molecules';

import { cardTransformer } from '@/graphql/transformers';
import { capitalize, gtmPush } from '@/utils';
import { post } from '@/utils/client';

import type { FunctionComponent } from 'react';
import type { TypeClusteringMapAlgoliaProps } from './types';

/**
 * Este número es la escala que se usa como referencia para calcular
 * el centro de los markers en el mapa cuando se despliega el Info View. Está
 * relacionado con DISPLACEMENT_REFERENCE.
 */
const SCALE_REFERENCE = 116.52824798807345;

/**
 * Este número es el desplacamiento que se usa como referencia para calcular
 * el centro de los markers en el mapa cuando se despliega el Info View. Está
 * relacionado con SCALE_REFERENCE.
 */
const DISPLACEMENT_REFERENCE = 0.2;

/**
 * Calculate the width of 1 map pixel in meters
 * Based on latitude and zoom level
 * @see https://groups.google.com/d/msg/google-maps-js-api-v3/hDRO4oHVSeM/osOYQYXg2oUJ
 * @param latitude
 * @param zoom
 * @returns
 */
const getScale = (latitude: number, zoom: number): number =>
  (156543.03392 * Math.cos((latitude * Math.PI) / 180)) / Math.pow(2, zoom);

/**
 * ClusteringMap
 */
export const ClusteringMapAlgolia: FunctionComponent<
  TypeClusteringMapAlgoliaProps
> = ({ className = '' }: TypeClusteringMapAlgoliaProps) => {
  const { results } = useHits();

  const { refine } = usePagination();

  // TODO: Arreglar tipos
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const resultParams = results?.params;
  const tempUrl = parse(
    remove(
      remove(remove(resultParams, 'hitsPerPage'), 'maxValuesPerFacet'),
      'page'
    )
  );

  const params = stringify(tempUrl);
  const [data, setData] = useState<null | { hits: Record<string, unknown>[] }>(
    null
  );
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const mapRef = useRef<google.maps.Map | null>(null);

  const [ref, inView] = useInView({
    /* Optional options */
    triggerOnce: false,
    rootMargin: '0px 0px',
  });

  const unfilteredPoints = useMemo(
    () =>
      data && !error
        ? data.hits
            .map((hit: any) =>
              hit.latitud && hit.longitud
                ? {
                    type: 'Feature',
                    properties: {
                      cluster: false,
                      hit,
                      hitId: hit.codigoUnidadAlquiler,
                    },
                    geometry: {
                      type: 'Point',
                      coordinates: [
                        parseFloat(hit.longitud as string),
                        parseFloat(hit.latitud as string),
                      ],
                    },
                  }
                : null
            )
            .filter(Boolean)
        : [],
    [data, error]
  );

  //filter out the points that have a duplicate hitId
  const points = unfilteredPoints.filter(
    (point, index, self) =>
      index ===
      self.findIndex((t) => t?.properties?.hitId === point?.properties?.hitId)
  );

  const [mapBounds, setMapBounds] = useState<any>(null);
  const [zoom, setZoom] = useState(14);
  const { clusters, supercluster } = useSupercluster({
    points: points,
    bounds: mapBounds,
    zoom,
    options: { radius: 70 },
  });

  // Reset page when map bounds change to avoid pagination issues
  useEffect(() => {
    refine(0);
  }, [mapBounds]);

  useEffect(() => {
    setLoading(true);
    post('/api/flats', {
      params: remove(params, 'insideBoundingBox'),
    })
      .then((data) => setData(data))
      .catch((error) => setError(error))
      .finally(() => setLoading(false));
  }, [tempUrl.facets, tempUrl.facetFilters, tempUrl.numericFilters]);

  const LoadingComponent = (
    // TODO: Agregar animación de carga
    <div
      className={`flex items-center justify-center bg-neutrals-50 ${className}`}
    >
      <div className="flex w-max flex-row items-center justify-between gap-2">
        <div className="h-2 w-2 rounded-full bg-neutrals-900" />
        <div className="h-2 w-2 rounded-full bg-neutrals-900" />
        <div className="h-1.5 w-1.5 rounded-full bg-neutrals-500" />
      </div>
    </div>
  );

  return !isLoading && data && !error ? (
    <>
      <style>{`
        .gmnoprint.gm-bundled-control {
          right: 52px !important;
          top: 16px !important;
        }

        .gmnoprint.gm-bundled-control .gmnoprint div {
          height: auto !important;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          background: none !important;
          box-shadow: none !important;
          gap: 12px;
        }

        .gmnoprint.gm-bundled-control .gmnoprint div > button + div {
          display: none;
        }

        .gmnoprint.gm-bundled-control .gmnoprint div button {
          border-radius: 50% !important;
          background: #fff !important;
          width: 40px !important;
          height: 40px !important;
          box-shadow: 0px 4px 12px rgba(13, 31, 35, 0.16);
          outline: 1px solid #d9ebef; 
        }
      `}</style>

      {mapBounds && inView && (
        <Configure
          insideBoundingBox={[
            [mapBounds[1], mapBounds[0], mapBounds[3], mapBounds[2]],
          ]}
        />
      )}

      <GoogleMap
        apiKey={process.env.NEXT_PUBLIC_MAP_API_KEY ?? ''}
        defaultZoom={10}
        onGoogleApiLoaded={({ map }: { map: google.maps.Map }) => {
          mapRef.current = map;

          const bounds = new google.maps.LatLngBounds();

          points.forEach((point) => {
            if (point?.geometry)
              bounds.extend({
                lng: point.geometry.coordinates[0],
                lat: point.geometry.coordinates[1],
              });
          });

          map.fitBounds(bounds);

          const listener = google.maps.event.addListener(
            map,
            'bounds_changed',
            () => {
              const zoom = map.getZoom();

              if (zoom && zoom > 20) map.setZoom(17);

              google.maps.event.removeListener(listener);
            }
          );

          map.setOptions({
            zoomControlOptions: {
              position: google.maps.ControlPosition.RIGHT_TOP,
            },
          });
        }}
        defaultCenter={{ lat: 40.2085, lng: -3.713 }}
        onChange={({ bounds, zoom }: any) => {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();

          setZoom(zoom);
          setMapBounds([sw.lng(), sw.lat(), ne.lng(), ne.lat()]);
        }}
        options={{
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoom: zoom,
          disableDefaultUI: false,
          clickableIcons: false,
        }}
        containerProps={{ className }}
        loadingContent={LoadingComponent}
        idleContent={LoadingComponent}
        errorContent={LoadingComponent}
        ref={ref}
      >
        {clusters.map((cluster) => {
          const [longitude, latitude] = cluster.geometry.coordinates;
          const {
            cluster: isCluster,
            point_count: pointCount,
            hit,
            hitId,
          } = cluster.properties;

          return isCluster ? (
            <Cluster
              key={`cluster-${cluster.id}`}
              lat={latitude}
              lng={longitude}
              count={pointCount}
              mapRef={mapRef}
              cluster={cluster}
              superCluster={supercluster}
            />
          ) : (
            <Marker
              key={hitId}
              lat={latitude}
              lng={longitude}
              onClick={() => {
                if (mapRef?.current) {
                  const map = mapRef.current;
                  const zoom = map.getZoom();

                  if (zoom) {
                    const scale = getScale(latitude, zoom);
                    const multiplier =
                      (scale * DISPLACEMENT_REFERENCE) / SCALE_REFERENCE;

                    gtmPush({
                      event: 'map_interaction',
                      map_action: 'select',
                      item_id: hit?.codigoUnidadAlquiler,
                      item_name: hit?.nombreUE,
                      item_type: capitalize(hit?.ventaAlquiler),
                    });
                    map.setCenter({
                      lat: latitude - multiplier,
                      lng: longitude + multiplier,
                    });
                  }
                }
              }}
            >
              {({ close, isOpen }) => (
                <div className={`relative w-[300px] ${isOpen ? '' : 'mt-6'}`}>
                  <CardFlat
                    {...cardTransformer(
                      hit,
                      // eslint-disable-next-line @typescript-eslint/no-empty-function
                      () => {}
                    )}
                    className="max-w-xs"
                  />

                  <button
                    className="absolute left-4 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-neutrals-0/60 hover:bg-neutrals-0/80"
                    onClick={close}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </Marker>
          );
        })}
      </GoogleMap>
    </>
  ) : (
    LoadingComponent
  );
};

ClusteringMapAlgolia.displayName = 'ClusteringMapAlgolia';
